import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import { useVendorFilter } from "@/contexts/VendorFilterContext";
import "./radar-hero.css";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

// Severity → color for the pin ring + label chip
function pinColor(p) {
  if (p.critical > 0) return { ring: "#ef4444", chip: "bg-red-500 text-white",     label: "Life Safety" };
  if (p.urgent > 0)   return { ring: "#f59e0b", chip: "bg-amber-500 text-white",   label: "Urgent" };
  if (p.active > 0)   return { ring: "#06b6d4", chip: "bg-cyan-500 text-slate-950", label: "Active" };
  if (p.pool > 0)     return { ring: "#94a3b8", chip: "bg-slate-500 text-white",   label: "Pool" };
  return { ring: "#10b981", chip: "bg-emerald-500 text-white", label: "Routine" };
}

function makePinIcon(p, phaseSec) {
  const c = pinColor(p);
  const size = 34 + Math.min(16, Math.log2(1 + p.total) * 4);
  const showCount = p.total > 1;
  // phaseSec offsets the "radar-hit" pulse per pin so that pins on the
  // west side of the map light up first, then the east — the animation
  // effectively rides on the back of the sweep cone.
  return L.divIcon({
    className: "fr-pin-icon",
    html: `
      <div class="fr-pin-outer" style="--ring:${c.ring};--phase:${(-phaseSec).toFixed(2)}s;width:${size}px;height:${size}px">
        <div class="fr-pin-ping" style="border-color:${c.ring}"></div>
        <div class="fr-pin-core" style="background:${c.ring};color:${c.ring}">
          ${showCount ? `<span class="fr-pin-count">${p.total}</span>` : ""}
        </div>
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

export default function RadarHero() {
  const nav = useNavigate();
  const { asQuery: vendorQuery, selected: selectedVendors } = useVendorFilter();
  const [pins, setPins] = useState([]);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("fr_radar_hero_collapsed") === "1"; } catch { return false; }
  });
  const [interactive, setInteractive] = useState(false);   // mobile tap-to-activate gate
  const mapRef = useRef(null);

  const toggleCollapsed = () => {
    setCollapsed(c => {
      const next = !c;
      try { localStorage.setItem("fr_radar_hero_collapsed", next ? "1" : "0"); } catch {}
      return next;
    });
  };

  useEffect(() => {
    let alive = true;
    const url = "/radar/pins" + (vendorQuery ? `?vendors=${encodeURIComponent(vendorQuery)}` : "");
    api.get(url)
      .then(r => { if (alive) setPins(r.data.pins || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [vendorQuery]);

  // Compute a sensible bounding box from the pins so the map auto-frames the portfolio
  const bounds = useMemo(() => {
    if (pins.length === 0) return null;
    const lats = pins.map(p => p.lat), lngs = pins.map(p => p.lng);
    return [[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]];
  }, [pins]);

  // Fit bounds when pins load
  useEffect(() => {
    if (mapRef.current && bounds) {
      try { mapRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 }); } catch {}
    }
  }, [bounds]);

  // Roll-up counts for the top-left status chip (unique of stats)
  const rollup = useMemo(() => pins.reduce((a, p) => {
    a.total += p.total; a.critical += p.critical; a.urgent += p.urgent; return a;
  }, { total: 0, critical: 0, urgent: 0 }), [pins]);

  // Center fallback — Philly if we have nothing yet
  const center = pins.length > 0 ? [pins[0].lat, pins[0].lng] : [39.9526, -75.1652];
  const tileUrl = MAPBOX_TOKEN
    ? `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`
    : "https://tile.openstreetmap.org/{z}/{x}/{y}.png";

  // Collapsed compact bar — recover screen space + escape hatch when the
  // full map trapped mobile scroll gestures.
  if (collapsed) {
    return (
      <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border-2 border-cyan-500/30 bg-slate-950 px-3 py-2 text-cyan-300"
        data-testid="radar-hero-collapsed">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"/>
          <span className="text-[10px] font-black uppercase tracking-widest truncate">
            Field Radar · {pins.length} sites · {rollup.total} WOs
            {rollup.critical > 0 && <span className="ml-2 text-red-400">· {rollup.critical} life safety</span>}
            {selectedVendors.length > 0 && (
              <span className="ml-2 text-cyan-200">· filtered by {selectedVendors.length} vendor{selectedVendors.length === 1 ? "" : "s"}</span>
            )}
          </span>
        </div>
        <button onClick={toggleCollapsed}
          data-testid="radar-hero-expand-btn"
          className="text-[10px] font-black uppercase tracking-widest text-cyan-200 hover:text-white px-2 py-1 border border-cyan-500/40 hover:bg-cyan-500/10 whitespace-nowrap">
          Show map
        </button>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-2xl mb-4 shadow-xl border border-cyan-500/25 bg-slate-950 fr-hero" data-testid="radar-hero-map">
      <div className="fr-hero-map">
        <MapContainer
          center={center}
          zoom={12}
          zoomControl={false}
          attributionControl={false}
          scrollWheelZoom={false}
          dragging={interactive}
          touchZoom={interactive}
          doubleClickZoom={interactive}
          tap={interactive}
          className="w-full h-full"
          whenCreated={(m) => { mapRef.current = m; }}
          bounds={bounds || undefined}
          boundsOptions={{ padding: [40, 40], maxZoom: 14 }}
        >
          <TileLayer
            url={tileUrl}
            tileSize={512}
            zoomOffset={-1}
            attribution=""
          />
          {pins.map(p => {
            const c = pinColor(p);
            // Longitude-based phase: west pins hit first (t=0), east pins last (t=4s).
            // Sweep cone runs -28° → +28° over 4s (alternate). Longitude of the
            // building maps 1:1 into the sweep's angular position, so the pulse
            // rides on the sweep like a proper radar scope.
            const lngs = pins.map(x => x.lng);
            const [lmin, lmax] = [Math.min(...lngs), Math.max(...lngs)];
            const span = Math.max(0.0001, lmax - lmin);
            const phase = ((p.lng - lmin) / span) * 4; // seconds
            return (
              <Marker
                key={p.address_key}
                position={[p.lat, p.lng]}
                icon={makePinIcon(p, phase)}
                eventHandlers={{
                  click: () => nav(`/admin/jobs?q=${encodeURIComponent(p.address.split(",")[0])}`),
                }}
                title={`${p.address} · ${p.total} WOs · ${c.label}`}
              >
                <Tooltip direction="top" offset={[0, -18]} opacity={1} className="fr-pin-tip">
                  <div className="fr-tip-inner" onClick={(e) => e.stopPropagation()}>
                    <div className="fr-tip-addr">{p.address.split(",")[0]}</div>
                    <div className="fr-tip-line">
                      <span className="fr-tip-count" style={{color: c.ring}}>{p.total}</span>
                      <span className="fr-tip-label">WOs</span>
                      {p.critical > 0 && <span className="fr-tip-chip fr-tip-red">{p.critical} critical</span>}
                      {p.active > 0 && <span className="fr-tip-chip fr-tip-cyan">{p.active} active</span>}
                    </div>
                    {p.top_wo && (
                      <div className="fr-tip-wo">
                        {p.top_wo.photo_url && (
                          <img src={p.top_wo.photo_url} alt="" className="fr-tip-photo"
                               onError={(e) => { e.target.style.display = "none"; }}/>
                        )}
                        <div className="fr-tip-wo-text">
                          {p.top_wo.wo && <span className="fr-tip-wo-num">WO#{p.top_wo.wo}</span>}
                          <div className="fr-tip-wo-title">{p.top_wo.title || "(untitled)"}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </Tooltip>
              </Marker>
            );
          })}
        </MapContainer>
      </div>

      {/* Dark tint for legibility */}
      <div className="fr-hero-tint pointer-events-none"/>

      {/* Cyan sweep cone — over the map, doesn't block interaction */}
      <svg className="fr-hero-sweep pointer-events-none" viewBox="0 0 400 400" preserveAspectRatio="none">
        <defs>
          <linearGradient id="fr-fan-grad-live" x1="50%" y1="100%" x2="50%" y2="0%">
            <stop offset="0%"  stopColor="#22d3ee" stopOpacity="0.7"/>
            <stop offset="60%" stopColor="#22d3ee" stopOpacity="0.25"/>
            <stop offset="100%" stopColor="#22d3ee" stopOpacity="0"/>
          </linearGradient>
        </defs>
        <path d="M200,400 L60,80 A280,280 0 0 1 340,80 Z"
              fill="url(#fr-fan-grad-live)" stroke="#22d3ee" strokeWidth="2" strokeOpacity="0.65"/>
      </svg>

      {/* Top-left live indicator */}
      <div className="fr-hero-chip pointer-events-none">
        <span className="w-2 h-2 rounded-full bg-[#00C4CC] animate-pulse"/>
        <span className="text-[10px] font-black uppercase tracking-widest text-cyan-300">
          Field Radar · {pins.length} sites · {rollup.total} WOs
          {selectedVendors.length > 0 && (
            <span className="ml-1 text-cyan-200">· {selectedVendors.length} vendor filter</span>
          )}
        </span>
      </div>

      {rollup.critical > 0 && (
        <div className="fr-hero-alert pointer-events-none">
          <span className="text-[10px] font-black uppercase tracking-widest text-white">
            {rollup.critical} Life Safety
          </span>
        </div>
      )}

      {/* Close/collapse button — always tappable so mobile users have an
          escape hatch when the map's grabbing their scroll gesture. */}
      <button
        onClick={toggleCollapsed}
        data-testid="radar-hero-collapse-btn"
        aria-label="Hide radar map"
        className="absolute top-2 right-2 z-[600] w-8 h-8 rounded-full bg-slate-950/80 hover:bg-slate-900 border border-cyan-500/40 flex items-center justify-center text-cyan-200 hover:text-white"
        style={rollup.critical > 0 ? { top: 44 } : undefined}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>

      {/* Mobile "tap to interact" veil — until the user taps, touch gestures
          fall through to page scroll instead of dragging the map. */}
      {!interactive && (
        <button
          type="button"
          onClick={() => setInteractive(true)}
          data-testid="radar-hero-activate"
          className="absolute inset-0 z-[550] flex items-end justify-center pb-3 md:hidden bg-transparent"
          aria-label="Enable radar map interactions"
        >
          <span className="text-[10px] font-black uppercase tracking-widest text-cyan-200 bg-slate-950/70 border border-cyan-500/40 rounded-full px-3 py-1 backdrop-blur">
            Tap to interact
          </span>
        </button>
      )}
    </div>
  );
}
